import type React from "react"
import { Bar } from "react-chartjs-2"
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from "chart.js"
import { TrendingUp } from "lucide-react"

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)

interface RatingDataItem {
  category: string
  score: number
}

interface RatingsOverviewProps {
  data: RatingDataItem[]
}

const RatingsOverview: React.FC<RatingsOverviewProps> = ({ data }) => {
  const colors = [
    "#3B82F6", // Blue
    "#8B5CF6", // Purple
    "#10B981", // Emerald
    "#F59E0B", // Amber
    "#EF4444", // Red
    "#06B6D4", // Cyan
  ]

  const chartData = {
    labels: data.map((item) => item.category),
    datasets: [
      {
        data: data.map((item) => item.score),
        backgroundColor: colors.slice(0, data.length).map((color) => `${color}20`),
        borderColor: colors.slice(0, data.length),
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
      },
    ],
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#fff",
        bodyColor: "#fff",
        borderColor: "#374151",
        borderWidth: 1,
        cornerRadius: 8,
        callbacks: {
          label: (context: any) => {
            const value = context.raw as number
            return `Score: ${value.toFixed(2)}`
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: "#64748B",
          font: {
            size: 12,
            weight: "500" as const,
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: "#F1F5F9",
          drawBorder: false,
        },
        ticks: {
          color: "#64748B",
          font: {
            size: 12,
          },
        },
      },
    },
  }

  const maxScore = Math.max(...data.map((item) => item.score))
  const avgScore = data.reduce((sum, item) => sum + item.score, 0) / data.length

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <TrendingUp className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-slate-800">Ratings Overview</h3>
            <p className="text-sm text-slate-600">Your performance across key areas</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-slate-50 rounded-xl p-4">
          <p className="text-sm text-slate-600 mb-1">Highest Score</p>
          <p className="text-2xl font-bold text-slate-800">{maxScore.toFixed(1)}</p>
        </div>
        <div className="bg-slate-50 rounded-xl p-4">
          <p className="text-sm text-slate-600 mb-1">Average Score</p>
          <p className="text-2xl font-bold text-slate-800">{avgScore.toFixed(1)}</p>
        </div>
      </div>

      {/* Chart */}
      <div className="h-80">
        <Bar data={chartData} options={options} />
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4">
        {data.map((item, index) => (
          <div key={item.category} className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: colors[index] }} />
            <span className="text-sm text-slate-600">{item.category}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default RatingsOverview
